import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type {
  SubjectSelectionTask,
  SubjectSelectionTaskForm,
  SubjectSelectionRule,
  SubjectSelectionValidationResult,
  SubjectSelectionLegalCombination
} from '@/types'
import { subjectSelectionApi } from '@/api/subject-selection'
import { useTaskOutcomeDismiss } from '@/composables/useTaskOutcomeDismiss'
import { registerTenantScopedReset } from '@/stores/_tenantScopedRegistry'

export const useSubjectSelectionStore = defineStore('subject-selection', () => {
  const tasks = ref<SubjectSelectionTask[]>([])
  const currentTaskId = ref<string | null>(null)
  const currentView = ref<'tasks' | 'detail'>('tasks')
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const { shouldShowOutcome, dismissTaskOutcome, acknowledgeTaskOutcome } =
    useTaskOutcomeDismiss('subjectSelection.dismissedOutcomes')
  const rules = ref<SubjectSelectionRule[]>([])
  const legalCombinations = ref<SubjectSelectionLegalCombination[]>([])
  const latestValidation = ref<SubjectSelectionValidationResult | null>(null)

  const currentTask = computed(() => tasks.value.find((task) => task.id === currentTaskId.value) ?? null)

  async function fetchTasks(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      tasks.value = await subjectSelectionApi.listTasks()
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e))
    } finally {
      loading.value = false
    }
  }

  function selectTask(taskId: string): void {
    currentTaskId.value = taskId
    currentView.value = 'detail'
  }

  function backToTasks(): void {
    currentView.value = 'tasks'
    currentTaskId.value = null
  }

  async function createTask(form: SubjectSelectionTaskForm): Promise<SubjectSelectionTask> {
    const task = await subjectSelectionApi.createTask(form)
    tasks.value.unshift(task)
    return task
  }

  async function updateTask(taskId: string, form: Partial<SubjectSelectionTaskForm>): Promise<SubjectSelectionTask | null> {
    const index = tasks.value.findIndex((task) => task.id === taskId)
    if (index === -1) return null
    const updated = await subjectSelectionApi.updateTask(taskId, form)
    tasks.value[index] = updated
    return tasks.value[index]
  }

  async function publishTask(taskId: string): Promise<SubjectSelectionTask | null> {
    const index = tasks.value.findIndex((task) => task.id === taskId)
    const published = await subjectSelectionApi.publishTask(taskId)
    if (index >= 0) {
      tasks.value[index] = published
    } else {
      tasks.value.unshift(published)
    }
    return published
  }

  async function deleteTask(taskId: string): Promise<void> {
    await subjectSelectionApi.removeTask(taskId)
    const index = tasks.value.findIndex((task) => task.id === taskId)
    if (index === -1) return
    tasks.value.splice(index, 1)
    if (currentTaskId.value === taskId) {
      backToTasks()
    }
  }

  async function fetchRules(taskId: string): Promise<void> {
    rules.value = await subjectSelectionApi.listRules(taskId)
  }

  async function createRule(taskId: string, payload: Omit<SubjectSelectionRule, 'id' | 'systemGenerated'>): Promise<void> {
    await subjectSelectionApi.createRule(taskId, payload)
    await fetchRules(taskId)
  }

  async function updateRule(
    taskId: string,
    ruleId: number,
    payload: Omit<SubjectSelectionRule, 'id' | 'systemGenerated'>
  ): Promise<void> {
    await subjectSelectionApi.updateRule(taskId, ruleId, payload)
    await fetchRules(taskId)
  }

  async function deleteRule(taskId: string, ruleId: number): Promise<void> {
    await subjectSelectionApi.deleteRule(taskId, ruleId)
    await fetchRules(taskId)
  }

  async function applyTemplate(taskId: string): Promise<void> {
    const { task, rules: newRules } = await subjectSelectionApi.applyTemplate(taskId)
    rules.value = newRules
    const index = tasks.value.findIndex((t) => t.id === taskId)
    if (index >= 0) {
      tasks.value[index] = task
    }
  }

  async function generateLegalCombinations(taskId: string): Promise<void> {
    legalCombinations.value = await subjectSelectionApi.generateLegalCombinations(taskId)
  }

  async function validateSelection(taskId: string, courseIds: number[]): Promise<void> {
    latestValidation.value = await subjectSelectionApi.validateSelection(taskId, courseIds)
  }

  async function submitSelection(taskId: string, studentId: number, courseIds: number[]): Promise<void> {
    latestValidation.value = await subjectSelectionApi.submitSelection(taskId, studentId, courseIds)
  }

  /** 切换租户 / 登出时清空本 Store，防止跨租户任务数据残留。 */
  function resetAll(): void {
    tasks.value = []
    currentTaskId.value = null
    currentView.value = 'tasks'
    loading.value = false
    error.value = null
    rules.value = []
    legalCombinations.value = []
    latestValidation.value = null
  }

  return {
    resetAll,
    tasks,
    currentTaskId,
    currentTask,
    currentView,
    loading,
    error,
    rules,
    legalCombinations,
    latestValidation,
    selectTask,
    backToTasks,
    createTask,
    updateTask,
    publishTask,
    deleteTask,
    fetchTasks,
    fetchRules,
    createRule,
    updateRule,
    deleteRule,
    applyTemplate,
    generateLegalCombinations,
    validateSelection,
    submitSelection,
    shouldShowOutcome,
    dismissTaskOutcome,
    acknowledgeTaskOutcome
  }
})

registerTenantScopedReset('subject-selection', () => useSubjectSelectionStore().resetAll())
